import { supabase } from "@/integrations/supabase/client";
import { clearNativePushToken } from "@/lib/nativePush";
import { resetAnalyticsIdentity } from "@/utils/analyticsIdentity";
import { captureError } from "@/utils/captureError";
import type { User, Session } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type UserPersona = Database["public"]["Enums"]["user_persona"];
type AppRole = Database["public"]["Enums"]["app_role"];

export interface AuthUser {
  id: string;
  email: string;
  profile: Profile | null;
  roles: AppRole[];
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  initialized: boolean;
}

// Map database persona enum to frontend persona type
export const mapDbPersonaToFrontend = (dbPersona: UserPersona): string => {
  const mapping: Record<UserPersona, string> = {
    'job_seeker': 'job-seeker',
    'individual_employer': 'individual-employer',
    'business_employer': 'business-employer',
    'provider': 'provider',
    'agency': 'agency',
  };
  return mapping[dbPersona] || 'job-seeker';
};

// Map frontend persona to database enum
export const mapFrontendPersonaToDb = (frontendPersona: string): UserPersona => {
  const mapping: Record<string, UserPersona> = {
    'job-seeker': 'job_seeker',
    'individual-employer': 'individual_employer',
    'business-employer': 'business_employer',
    'provider': 'provider',
    'agency': 'agency',
  };
  return mapping[frontendPersona] || 'job_seeker';
};

class AuthService {
  // Sign up with CNIC/email and password
  async signUp(
    email: string,
    password: string,
    metadata: {
      full_name: string;
      persona: string;
      cnic?: string;
      ntn?: string;
      phone?: string;
    }
  ): Promise<{ user: User | null; error: string | null }> {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: metadata.full_name,
            persona: mapFrontendPersonaToDb(metadata.persona),
            cnic: metadata.cnic,
            ntn: metadata.ntn,
            phone: metadata.phone,
          },
        },
      });

      if (error) {
        return { user: null, error: error.message };
      }

      return { user: data.user, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      return { user: null, error: message };
    }
  }

  // Finalize a CNIC/password signup by calling the atomic finalize_password_signup
  // RPC — the ONLY path that stamps persona_confirmed = true (plus active persona,
  // business fields, and worker_profiles). This wrapper makes the call robust:
  //   1. It waits for the session to become live — finalize is auth.uid()-bound,
  //      so a call issued before signUp's session has been persisted would run as
  //      anon and return not_authenticated.
  //   2. After the RPC returns ok, it re-reads the caller's own profile to verify
  //      persona_confirmed actually landed. A stale/lagging deployed function that
  //      returns ok WITHOUT setting the flag is exactly what left 12 July-2026 CNIC
  //      signups stuck at persona_confirmed = false; when that happens now we
  //      capture telemetry and retry the RPC once instead of failing silently.
  //
  // 2026-07-26 regression fix. The first version of this helper treated a missing
  // session as a terminal `not_authenticated` and returned WITHOUT calling the
  // RPC — turning a transient storage/session race into a hard signup failure and
  // leaving the profile at persona_confirmed = false. Every email/password signup
  // from 2026-07-22 17:00 UTC (when this helper went live) onward landed that way,
  // and it was invisible in PostHog because the only capture site sat AFTER the
  // RPC. Three changes close that hole:
  //   a) `knownUserId` — signUp already returns the new user, so the caller passes
  //      the id in and we no longer depend on reading it back out of storage.
  //   b) the session wait retries briefly instead of failing on the first miss,
  //      and a missing session no longer skips the RPC (finalize itself is the
  //      authority on whether auth.uid() resolved).
  //   c) EVERY failure path captures telemetry, so this class of silent failure
  //      can never again run for four days undetected.
  async finalizeSignup(params: {
    persona: UserPersona;
    companyName?: string | null;
    isRegisteredBusiness?: boolean | null;
    knownUserId?: string | null;
  }): Promise<{ ok: boolean; error?: string }> {
    const rpcArgs = {
      _persona: params.persona,
      _company_name: params.companyName ?? null,
      _is_registered_business: params.isRegisteredBusiness ?? null,
    };

    // 1. Give the session a moment to become readable. signUp resolves once the
    //    session is issued, but persistence to storage is not instantaneous on
    //    every platform (notably the Android WebView).
    let userId = params.knownUserId ?? null;
    for (let attempt = 0; attempt < 3 && !userId; attempt++) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        userId = sessionData.session?.user?.id ?? null;
      } catch {
        /* fall through to the retry / RPC attempt below */
      }
      if (!userId) await new Promise((r) => setTimeout(r, 250));
    }

    // 2. Call the atomic finalizer regardless. If auth.uid() genuinely has not
    //    resolved, finalize returns { ok:false, error:'not_authenticated' } and we
    //    surface that as a real, captured failure — never a silent skip.
    const { data, error } = await supabase.rpc('finalize_password_signup' as any, rpcArgs);
    if (error) {
      captureError('signup-finalize-failed', error, { persona: params.persona, stage: 'rpc_error' });
      return { ok: false, error: error.message };
    }
    const result = data as { ok?: boolean; error?: string } | null;
    if (!result?.ok) {
      captureError(
        'signup-finalize-failed',
        new Error(result?.error || 'finalize_password_signup returned not-ok'),
        { persona: params.persona, stage: 'rpc_not_ok', hadSession: !!userId },
      );
      return { ok: false, error: result?.error || 'Could not finalize account' };
    }

    // 3. Verify persona_confirmed actually flipped. Best-effort: a failure here must
    //    never block an otherwise-successful signup, only surface + self-heal it.
    try {
      if (!userId) {
        const { data: sessionData } = await supabase.auth.getSession();
        userId = sessionData.session?.user?.id ?? null;
      }
      if (!userId) return { ok: true };
      const { data: prof } = await supabase
        .from('profiles')
        .select('persona_confirmed')
        .eq('id', userId)
        .maybeSingle();
      if (prof && prof.persona_confirmed === false) {
        captureError(
          'signup-persona-not-confirmed',
          new Error('finalize_password_signup returned ok but persona_confirmed is still false'),
          { persona: params.persona },
        );
        // One retry — covers a transient miss; if the deployed function is stale
        // this is a no-op but the telemetry above makes the gap visible.
        await supabase.rpc('finalize_password_signup' as any, rpcArgs);
      }
    } catch {
      /* verification is advisory only */
    }

    return { ok: true };
  }

  // Sign in with email and password
  async signIn(
    email: string,
    password: string
  ): Promise<{ user: User | null; session: Session | null; error: string | null }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { user: null, session: null, error: error.message };
      }

      return { user: data.user, session: data.session, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      return { user: null, session: null, error: message };
    }
  }

  // Sign out
  // When preserveBiometric is true, perform a client-only signout so the
  // server refresh token remains valid for biometric re-authentication.
  async signOut(preserveBiometric = false): Promise<{ error: string | null }> {
    try {
      // Native (Capacitor) only: evict this device's FCM token from the
      // logging-out user's behavior_data BEFORE the session is torn down
      // (clearNativePushToken reads auth.getUser(), which needs a live
      // session). No-op on web / when no token is stored. Belt-and-suspenders
      // to the login-time exclusive claim (claim_fcm_token) so a shared device
      // can't ring the previous user's pushes between logout and the next login.
      await clearNativePushToken();

      // Drop the PostHog identity with the session. Without this the device
      // keeps reporting as the user who just left, so the NEXT person to sign
      // in on it has their events attributed to the previous account. Placed
      // here, alongside clearNativePushToken, so it covers both real-logout
      // callers (UserContext.logout and useSupabaseAuth) and — by construction
      // — can never fire on the scope:'local' session-swap paths, which do not
      // call this method. See src/utils/analyticsIdentity.ts.
      resetAnalyticsIdentity();

      if (preserveBiometric) {
        if (typeof window !== 'undefined') {
          const authKeys = Object.keys(window.localStorage).filter(
            (key) => key.startsWith('sb-') && key.includes('-auth-token')
          );

          for (const key of authKeys) {
            window.localStorage.removeItem(key);
          }
        }

        return { error: null };
      }

      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign out failed';
      return { error: message };
    }
  }

  // Get current session
  async getSession(): Promise<{ session: Session | null; error: string | null }> {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        return { session: null, error: error.message };
      }
      return { session: data.session, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get session';
      return { session: null, error: message };
    }
  }

  // Get user profile from profiles table
  async getProfile(userId: string): Promise<{ profile: Profile | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        return { profile: null, error: error.message };
      }

      return { profile: data, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get profile';
      return { profile: null, error: message };
    }
  }

  // Get user roles from user_roles table
  async getUserRoles(userId: string): Promise<{ roles: AppRole[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        return { roles: [], error: error.message };
      }

      const roles = data.map((r) => r.role);
      return { roles, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get roles';
      return { roles: [], error: message };
    }
  }

  // Update user profile
  async updateProfile(
    userId: string,
    updates: Partial<Omit<Profile, 'id' | 'created_at'>>
  ): Promise<{ profile: Profile | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { profile: null, error: error.message };
      }

      return { profile: data, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      return { profile: null, error: message };
    }
  }

  // Check if user is admin (server-side validated)
  async isAdmin(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('is_admin', { _user_id: userId });
      if (error) {
        if (import.meta.env.DEV) console.error('Error checking admin status:', error);
        return false;
      }
      return data === true;
    } catch {
      return false;
    }
  }

  // Check if user has a specific role
  async hasRole(userId: string, role: AppRole): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_role', { 
        _user_id: userId, 
        _role: role 
      });
      if (error) {
        if (import.meta.env.DEV) console.error('Error checking role:', error);
        return false;
      }
      return data === true;
    } catch {
      return false;
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email';
      return { error: message };
    }
  }

  // Update password
  async updatePassword(newPassword: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update password';
      return { error: message };
    }
  }

  // Get wallet for user
  async getWallet(userId: string) {
    try {
      const { data, error } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        return { wallet: null, error: error.message };
      }

      return { wallet: data, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get wallet';
      return { wallet: null, error: message };
    }
  }
}

export const authService = new AuthService();
